import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";

// ─── IMPORTED LAYOUT COMPONENTS ───────────────────────────────────────────────
import Sidebar from "./Includes/Sidebar";
import TopBar from "./Includes/TopBar";
import BottomNav from "./Includes/BottomNav";
import { logoutUser } from "./utils/auth";

const REDIRECT_WAIT_SECONDS = 1.5;

export default function DriverDashboardPage({ nav }) {
  const navigate = useNavigate();
  const location = useLocation();

  // ─── 1. SYNCHRONOUS SECURITY CHECK ───────────────────────────────────────
  const getLocalUser = () => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  };
  
  const localUser = getLocalUser();
  const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");

  // SECURITY BUG FIX (this keeps getting reverted in re-uploads — please
  // don't revert it again): this was `localUser.role === "driver"`, which
  // (a) is inverted — it should deny when the role does NOT match, not
  // when it does — and (b) checks a string that doesn't exist on the
  // backend at all. The real role choices (users/models.py
  // User.ROLE_CHOICES) are "pilot" | "company" | "dispatcher" | "admin" —
  // there is no "driver" role. Net effect of the buggy version: a
  // dispatcher/company/admin account loading this page is treated as
  // authenticated (none of those equal "driver" either), while a real
  // pilot/driver account only worked by accident.
  const isAuthInvalid = !token || !localUser || localUser.role !== "pilot";
  // ───────────────────────────────────────────────────────────────────────

  // ─── 2. STATE HYDRATION & HOOKS ──────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Initialize with localUser immediately to prevent name flickering on load
  const [userData, setUserData] = useState(localUser);
  const [profileCompletion, setProfileCompletion] = useState(0);

  const [isRedirecting, setIsRedirecting] = useState(isAuthInvalid);
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_WAIT_SECONDS);
  const startRef = useRef(null);

  const goToLogin = () => {
    if (typeof nav === "function") {
      nav("login");
    } else if (navigate) { 
      navigate("/login", { replace: true });
    } else {
      window.location.href = "/login";
    }
  };

  const handleLogout = () => {
    logoutUser(navigate); 
  };

  // Effect: Clear bad session data if role is incorrect
  useEffect(() => {
    if (isAuthInvalid && localUser && localUser.role !== "pilot") {
      localStorage.removeItem("token");
      localStorage.removeItem("user_token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
    }
  }, [isAuthInvalid, localUser]);

  // Effect: Fetch Fresh User Data from Django in the background
  useEffect(() => {
    const fetchProfileData = async () => {
      if (isAuthInvalid) return;

      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
        const userRes = await fetch(`${apiUrl}/api/me/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userJson = await userRes.json();

        const driverRes = await fetch(`${apiUrl}/api/profile/driver/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const driverJson = await driverRes.json();

        const combinedData = { ...userJson, driverProfile: driverJson };
        setUserData(combinedData);
        calculateCompletion(combinedData);
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };

    fetchProfileData();
  }, [isAuthInvalid, token]);

  // Effect: Drives the countdown + progress bar
  useEffect(() => {
    if (!isRedirecting) return;

    startRef.current = Date.now();
    const tick = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const remaining = Math.max(0, REDIRECT_WAIT_SECONDS - elapsed);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(tick);
        goToLogin();
      }
    }, 50);

    return () => clearInterval(tick);
  }, [isRedirecting]);

  const calculateCompletion = (data) => {
    const requiredFields = [
      data.first_name, data.last_name, data.phone,
      data.driverProfile?.vehicle_make,
      data.driverProfile?.vehicle_model,
      data.driverProfile?.primary_state
    ];
    const filledFields = requiredFields.filter(field => field && field.trim() !== "");
    const percentage = Math.round((filledFields.length / requiredFields.length) * 100);
    setProfileCompletion(percentage);
  };

  // ─── 3. EARLY RETURN FOR UNAUTHORIZED USERS ──────────────────────────────
  if (isRedirecting || isAuthInvalid) {
    const progressPct = ((REDIRECT_WAIT_SECONDS - secondsLeft) / REDIRECT_WAIT_SECONDS) * 100;

    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50 px-4" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center shadow-xl border border-gray-100">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 animate-pulse" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Secure session required</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            You aren't logged in, or your secure session has ended. Taking you to the login page.
          </p>

          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
            <div
              className="h-full bg-blue-600 rounded-full"
              style={{ width: `${progressPct}%`, transition: "width 0.05s linear" }}
            />
          </div>

          <div className="flex justify-center">
            <span className="text-sm font-semibold text-blue-600">
              Redirecting in {Math.ceil(secondsLeft)}s…
            </span>
          </div>

          <button
            onClick={goToLogin}
            className="mt-6 text-sm text-gray-400 hover:text-gray-600 underline underline-offset-2"
          >
            Go now instead
          </button>
        </div>
      </div>
    );
  }

  // ─── 4. MAIN AUTHORIZED LAYOUT ───────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <Sidebar 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen} 
        user={userData} 
        handleLogout={handleLogout} 
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopBar setSidebarOpen={setSidebarOpen} user={userData} />

        <main className="flex-1 overflow-x-hidden overflow-y-auto">
          {/* 
            👇 UPGRADED CONTAINER
            - Changed to max-w-7xl for a wider, proper desktop layout.
            - Added standard responsive padding (px-4 sm:px-6 lg:px-8 py-6 sm:py-8) 
              so pages don't get squished, and alignment is uniform across the app.
          */}
          <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <Outlet context={{ user: userData, setUserData, completion: profileCompletion }} />
          </div>
        </main>

        <BottomNav />
      </div>
    </div>
  );
}